"use client";

import { useEffect, useRef, useState } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { MenuIcon } from "lucide-react";
import { useTranslations } from "next-intl";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { SidebarContent } from "./app-sidebar";
import { BrandMark } from "./brand-mark";

interface MobileTopBarProps {
  isAdmin: boolean;
}

export function MobileTopBar({ isAdmin }: MobileTopBarProps) {
  const t = useTranslations("Sidebar");
  const tCommon = useTranslations("Common");
  const pathname = usePathname();
  const [open, setOpen] = useState(false);
  const [openedOnPathname, setOpenedOnPathname] = useState(pathname);
  const [isHidden, setIsHidden] = useState(false);
  const lastScrollY = useRef(0);
  const sheetOpen = open && openedOnPathname === pathname;
  const isVotingRoute =
    /^\/parties\/[^/]+\/vote(\/|$)/.test(pathname) ||
    /^\/parties\/[^/]+\/vote-for\/[^/]+(\/|$)/.test(pathname);
  const shouldHideHeader = isVotingRoute && !sheetOpen && isHidden;

  const openSheet = () => {
    setOpenedOnPathname(pathname);
    setIsHidden(false);
    setOpen(true);
  };

  useEffect(() => {
    const root = document.documentElement;
    if (!isVotingRoute) {
      root.style.setProperty("--voting-sticky-top", "3.5rem");
      return;
    }

    root.style.setProperty(
      "--voting-sticky-top",
      shouldHideHeader ? "0rem" : "3.5rem",
    );

    return () => {
      root.style.setProperty("--voting-sticky-top", "3.5rem");
    };
  }, [isVotingRoute, shouldHideHeader]);

  useEffect(() => {
    if (!isVotingRoute) return;
    lastScrollY.current = window.scrollY;

    const handleScroll = () => {
      if (sheetOpen) return;

      const currentY = window.scrollY;
      const delta = currentY - lastScrollY.current;

      if (currentY <= 24) {
        setIsHidden(false);
      } else if (delta > 6) {
        setIsHidden(true);
      } else if (delta < -6) {
        setIsHidden(false);
      }

      lastScrollY.current = currentY;
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [isVotingRoute, sheetOpen]);

  return (
    <>
      <header
        data-app-chrome=""
        className={`fixed inset-x-0 top-0 z-40 ap-pad-safe-top border-b border-(--mk-cream-08) bg-(--mk-ink) px-4 transition-transform duration-200 will-change-transform lg:hidden ${
          shouldHideHeader ? "-translate-y-full" : "translate-y-0"
        }`}
      >
        <div className="flex h-14 items-center justify-between">
          <Link href="/parties" className="flex items-center gap-2.5 no-underline" data-app-chrome-mark="">
            <BrandMark size={32} className="h-8 w-8" />
            <span className="font-serif text-lg font-bold tracking-tight text-(--mk-cream)">
              {tCommon("brand")}
            </span>
          </Link>
          <button
            type="button"
            onClick={openSheet}
            className="cursor-pointer rounded-lg p-2 text-(--mk-cream-60) transition-colors hover:bg-(--mk-cream-surface) hover:text-(--mk-cream)"
            aria-label={t("openMenu")}
          >
            <MenuIcon className="h-5 w-5" />
          </button>
        </div>
      </header>

      <Sheet
        open={sheetOpen}
        onOpenChange={(nextOpen) => {
          if (nextOpen) {
            openSheet();
            return;
          }
          setOpen(false);
        }}
      >
        <SheetContent
          side="left"
          className="w-72 bg-(--mk-ink)"
          onCloseAutoFocus={(event) => {
            event.preventDefault();
          }}
          onClick={(event) => {
            const target = event.target;
            if (!(target instanceof Element)) return;
            if (target.closest("a[href]")) {
              setOpen(false);
            }
          }}
        >
          <SheetTitle className="sr-only">{t("navigation")}</SheetTitle>
          <SidebarContent isAdmin={isAdmin} onNavigate={() => setOpen(false)} />
        </SheetContent>
      </Sheet>
    </>
  );
}
